import dayjs, { Dayjs } from "dayjs";
import {
  CalendarEvent,
  CalendarRange,
  CalendarView,
  ScheduleSummary,
} from "../../services/scheduling/Scheduling.types";

export const CALENDAR_START_HOUR = 0;
export const CALENDAR_END_HOUR = 24;
export const CALENDAR_HOUR_HEIGHT = 64;

const schedulePalette = [
  "#2563eb",
  "#7c3aed",
  "#0f766e",
  "#c2410c",
  "#be185d",
  "#0369a1",
  "#4d7c0f",
  "#9333ea",
];

export function startOfIsoWeek(value: Dayjs): Dayjs {
  const dayOffset = (value.day() + 6) % 7;
  return value.startOf("day").subtract(dayOffset, "day");
}

export function getCalendarRange(
  anchorDate: Dayjs,
  view: CalendarView,
): CalendarRange {
  if (view === "day") {
    return {
      start: anchorDate.startOf("day").toISOString(),
      end: anchorDate.startOf("day").add(1, "day").toISOString(),
    };
  }

  if (view === "week") {
    const start = startOfIsoWeek(anchorDate);
    return {
      start: start.toISOString(),
      end: start.add(7, "day").toISOString(),
    };
  }

  const start = startOfIsoWeek(anchorDate.startOf("month"));
  return {
    start: start.toISOString(),
    end: start.add(42, "day").toISOString(),
  };
}

export function getVisibleDays(anchorDate: Dayjs, view: CalendarView): Dayjs[] {
  if (view === "day") {
    return [anchorDate.startOf("day")];
  }

  const start =
    view === "week"
      ? startOfIsoWeek(anchorDate)
      : startOfIsoWeek(anchorDate.startOf("month"));
  const count = view === "week" ? 7 : 42;

  return Array.from({ length: count }, (_, index) => start.add(index, "day"));
}

export function moveCalendarAnchor(
  anchorDate: Dayjs,
  view: CalendarView,
  direction: -1 | 1,
): Dayjs {
  const unit = view === "day" ? "day" : view === "week" ? "week" : "month";
  return anchorDate.add(direction, unit);
}

export function periodLabel(anchorDate: Dayjs, view: CalendarView): string {
  if (view === "day") {
    return anchorDate.format("dddd D MMMM YYYY");
  }

  if (view === "month") {
    return anchorDate.format("MMMM YYYY");
  }

  const start = startOfIsoWeek(anchorDate);
  const end = start.add(6, "day");
  if (start.month() === end.month()) {
    return `${start.format("D")} – ${end.format("D MMMM YYYY")}`;
  }

  if (start.year() === end.year()) {
    return `${start.format("D MMMM")} – ${end.format("D MMMM YYYY")}`;
  }

  return `${start.format("D MMMM YYYY")} – ${end.format("D MMMM YYYY")}`;
}

export function getScheduleColor(
  scheduleId: string,
  schedules: ScheduleSummary[],
): string {
  const index = schedules.findIndex((schedule) => schedule.id === scheduleId);
  if (index >= 0) {
    return schedulePalette[index % schedulePalette.length];
  }

  const hash = Array.from(scheduleId).reduce(
    (value, character) => value + character.charCodeAt(0),
    0,
  );
  return schedulePalette[hash % schedulePalette.length];
}

export function eventsForDay(
  events: CalendarEvent[],
  day: Dayjs,
): CalendarEvent[] {
  const start = day.startOf("day");
  const end = start.add(1, "day");

  return events.filter((event) => {
    const eventStart = dayjs(event.start);
    const eventEnd = dayjs(event.end);
    return eventStart.isBefore(end) && eventEnd.isAfter(start);
  });
}

export function eventTimeLabel(event: CalendarEvent): string {
  return `${dayjs(event.start).format("HH:mm")} – ${dayjs(event.end).format("HH:mm")}`;
}

export interface PositionedCalendarEvent {
  event: CalendarEvent;
  lane: number;
  laneCount: number;
}

export function assignEventLanes(
  events: CalendarEvent[],
): PositionedCalendarEvent[] {
  const sorted = [...events].sort(
    (left, right) =>
      dayjs(left.start).valueOf() - dayjs(right.start).valueOf() ||
      dayjs(left.end).valueOf() - dayjs(right.end).valueOf(),
  );
  const result: PositionedCalendarEvent[] = [];
  let group: Array<{ event: CalendarEvent; lane: number }> = [];
  let laneEnds: number[] = [];
  let groupEnd = Number.NEGATIVE_INFINITY;

  const flushGroup = () => {
    const laneCount = Math.max(1, laneEnds.length);
    group.forEach(({ event, lane }) =>
      result.push({ event, lane, laneCount }),
    );
    group = [];
    laneEnds = [];
    groupEnd = Number.NEGATIVE_INFINITY;
  };

  sorted.forEach((event) => {
    const start = dayjs(event.start).valueOf();
    const end = dayjs(event.end).valueOf();

    if (group.length > 0 && start >= groupEnd) {
      flushGroup();
    }

    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }

    group.push({ event, lane });
    groupEnd = Math.max(groupEnd, end);
  });

  if (group.length > 0) {
    flushGroup();
  }

  return result;
}
